import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Grid,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MergeTypeIcon from "@mui/icons-material/MergeType";

import AccountDetailCard from "./AccountDetailCard";
import MergePreviewDialog from "./MergePreviewDialog";

/**
 * Expandable duplicate-group row: compact 2-column cards + fixed bottom action bar
 * so merge controls do not jump when the keeper radio changes.
 */
const DuplicateGroupRow = ({
  group,
  entityType,
  isSelected,
  onSelectionChange,
  onMerged,
}) => {
  const [open, setOpen] = useState(false);
  const [keeperId, setKeeperId] = useState(group.recommended_keeper_id);
  const [previewState, setPreviewState] = useState({ open: false, loserId: null });

  useEffect(() => {
    setKeeperId(group.recommended_keeper_id);
  }, [group.recommended_keeper_id]);

  const idField =
    entityType === "users"
      ? "user_id"
      : entityType === "law_firms"
      ? "lawfirm_id"
      : "pg_id";

  const accountIds = group.accounts.map((a) => a[idField]);
  const loserIds = accountIds.filter((id) => id !== keeperId);

  const handleKeeperChange = (newKeeperId) => {
    setKeeperId(newKeeperId);
    if (isSelected && onSelectionChange) {
      onSelectionChange(group.key, newKeeperId, accountIds.filter((id) => id !== newKeeperId));
    }
  };

  const handleCheckbox = (e) => {
    if (!onSelectionChange) return;
    if (e.target.checked) {
      onSelectionChange(group.key, keeperId, loserIds);
    } else {
      onSelectionChange(group.key, null, []);
    }
  };

  const summaryTitle = group.key || "(empty)";

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={!!isSelected}
            onChange={handleCheckbox}
          />
        </TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{summaryTitle}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {group.account_count} accounts in this group
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip size="small" label={`Keeper: ${keeperId}`} color="primary" variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip size="small" label={`${loserIds.length} loser${loserIds.length === 1 ? "" : "s"}`} color="warning" variant="outlined" />
        </TableCell>
        <TableCell align="right">
          <Button
            size="small"
            variant="outlined"
            startIcon={<MergeTypeIcon fontSize="small" />}
            disabled={loserIds.length === 0}
            onClick={() => {
              if (loserIds.length === 1) {
                setPreviewState({ open: true, loserId: loserIds[0] });
              } else {
                setOpen(true);
              }
            }}
          >
            {loserIds.length > 1 ? "Choose loser" : "Merge"}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                p: 1.5,
                pt: 1.25,
                backgroundColor: "rgba(37,99,235,0.04)",
                borderRadius: 1,
                mb: 0.5,
              }}
            >
              <Grid container spacing={1.25}>
                {group.accounts.map((acc) => {
                  const accId = acc[idField];
                  const isKeeper = accId === keeperId;
                  return (
                    <Grid item xs={12} sm={6} key={accId}>
                      <AccountDetailCard
                        account={acc}
                        entityType={entityType}
                        isKeeper={isKeeper}
                        isRecommended={accId === group.recommended_keeper_id}
                        onSelect={handleKeeperChange}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              {loserIds.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    pt: 1.25,
                    borderTop: "1px solid rgba(37, 99, 235, 0.2)",
                    backgroundColor: "rgba(255,255,255,0.7)",
                    borderRadius: 1,
                    px: 1,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
                    Merge into keeper <strong>{keeperId}</strong> — pick the duplicate account to remove:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {loserIds.map((lid) => (
                      <Button
                        key={lid}
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<MergeTypeIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setPreviewState({ open: true, loserId: lid })}
                        sx={{ textTransform: "none" }}
                      >
                        Merge loser {lid}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <MergePreviewDialog
        open={previewState.open}
        onClose={() => setPreviewState({ open: false, loserId: null })}
        mergeType={
          entityType === "users"
            ? "portal_user"
            : entityType === "law_firms"
            ? "law_firm"
            : "professional_guardian"
        }
        keeperId={keeperId}
        loserId={previewState.loserId}
        onMerged={onMerged}
      />
    </>
  );
};

export default DuplicateGroupRow;
